import React, { useState, useEffect, useRef } from 'react';

interface AnimatedNumberProps {
  value: number;
  duration?: number;
  className?: string;
  prefix?: string;
  suffix?: string;
  decimals?: number;
}

const AnimatedNumber: React.FC<AnimatedNumberProps> = ({
  value,
  duration = 800,
  className = '',
  prefix = '',
  suffix = '',
  decimals = 2,
}) => {
  const [displayValue, setDisplayValue] = useState(value);
  const [trend, setTrend] = useState<'none' | 'up' | 'down'>('none');
  const animationRef = useRef<number | null>(null);

  useEffect(() => {
    const startValue = displayValue;
    const endValue = value;

    if (startValue === endValue) return;

    // Schedule trend update safely to avoid ESLint error
    const newTrend = endValue > startValue ? 'up' : 'down';
    const trendTimer = setTimeout(() => setTrend(newTrend), 0);

    const startTime = performance.now();
    let resetTimer: NodeJS.Timeout;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Power3 easeOut function for a premium feel
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      const nextValue = startValue + (endValue - startValue) * easeProgress;
      setDisplayValue(nextValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // Reset trend after animation finishes
        resetTimer = setTimeout(() => setTrend('none'), 1000);
      }
    };

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      clearTimeout(trendTimer);
      if (resetTimer) clearTimeout(resetTimer);
    };
  }, [value, duration]);

  const formattedValue = displayValue.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  const getTrendStyle = () => {
    if (trend === 'up') return { color: '#00e676', transition: 'color 0.2s' };
    if (trend === 'down') return { color: '#ff4d4d', transition: 'color 0.2s' };
    return { transition: 'color 1s' };
  };

  return (
    <span className={className} style={getTrendStyle()}>
      {prefix}
      {formattedValue}
      {suffix}
    </span>
  );
};

export default AnimatedNumber;
